import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { names } from 'Constants';
import {
	usePartnerDetails,
	useResponsive,
	useSearchWithAnalyticsJobs
} from 'Hooks';
import { clx, config } from 'Utils';
import { Colors } from 'Types';
import {
	Card,
	DecorationTab,
	FontAwesome,
	ShimmerImage,
	ProfileDecorationBusinessPartnerBadge,
	Button,
	Badge
} from 'Elements';
import styles from 'partials/decoration/page-decoration-box.module.scss';
import { SkeletonPartnerDecorationBox } from './SkeletonPartnerDecorationBox';

export const PartnerDecorationBox: FC = () => {
	const { partners } = names;
	const { t } = useTranslation();
	const { partner_id } = useParams();
	const { pathname } = useLocation();
	const { isTabletAndAbove, isDesktopAndAbove } = useResponsive();
	const { data: fields, isFetching } = usePartnerDetails(
		partner_id as string
	);
	const getImageSize = () => {
		return isDesktopAndAbove ? 160 : isTabletAndAbove ? 136 : 112;
	};
	const defaultImage = '/assets/img/default-image.png';
	const { data } = useSearchWithAnalyticsJobs({
		tenantId: partner_id as string,
		keyword: null,
		jobStatus: ''
	});

	const defaultTab =
		pathname === `${partners.path}/${partner_id}` ||
		pathname === `${partners.path}/${partner_id}/general`;

	const tabs = [
		{
			id: 0,
			title: 'tab.general',
			path: 'general',
			isActive: defaultTab
		},
		{
			id: 1,
			title: 'tab.jobs',
			path: 'jobs',
			isActive: pathname === `${partners.path}/${partner_id}/jobs`
		}
	];

	type PartnerFields = {
		id?: string;
		slugUrl?: string;
		// ...other fields as needed
	};


	return isFetching ? (
		<SkeletonPartnerDecorationBox />
	) : (
		<Card tab className="mb-4">
			<div className="d-flex justify-content-between align-items-start">
				<div
					className={clx(
						styles.body,
						'd-flex flex-column flex-md-row gap-3'
					)}>
					<div tabIndex={-1} className={styles.logo}>
						<div className={styles.logoBox}>
							{isFetching ? (
								<div
									style={{
										width: getImageSize(),
										height: getImageSize(),
										borderRadius: 8,
										background:
											'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
										animation:
											'skeleton-loading 1.2s infinite linear'
									}}
								/>
							) : (
								<ShimmerImage
									width={getImageSize()}
									height={getImageSize()}
									src={fields?.logo ?? (defaultImage as any)}
									defaultImage={defaultImage}
									className={styles.shimmer}
									alt={fields?.businessName ?? ''}
								/>
							)}
						</div>
					</div>
					<div className="flex-grow-1">
						{isFetching ? (
							<div
								style={{
									width: '60%',
									height: 24,
									borderRadius: 4,
									background:
										'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
									animation:
										'skeleton-loading 1.2s infinite linear',
									marginBottom: 8
								}}
							/>
						) : (
							<h3 className="h5 lh-sm fw-400 text-break">
								{fields?.businessName}
							</h3>
						)}
						<div className="d-flex align-items-center flex-wrap gap-3 mb-3">
							{isFetching ? (
								<div
									style={{
										width: 100,
										height: 20,
										borderRadius: 4,
										background:
											'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
										animation:
											'skeleton-loading 1.2s infinite linear'
									}}
								/>
							) : (
								<ProfileDecorationBusinessPartnerBadge
									profile={fields as any}
								/>
							)}
						</div>
					</div>
				</div>
				<div className="d-flex align-items-center flex-column">
					<Card>
						<div className="d-flex flex-column text-center  text-primary gap-2">
							<span>{t('label.total-views')}</span>

							<h2 className="fw-bold m-0">
								{data?.reduce(
									(acc, job) => acc + (job.totalView || 0),
									0
								) ?? 0}
							</h2>
							<span>{t('label.views-in-past-year')}</span>
						</div>
					</Card>
					{/* <Badge
						color={Colors['warning']}
						className="mt-2 w-100 p-1">
						{t('label.total-views')}: &nbsp;
						{data?.reduce(
							(acc, job) => acc + (job.totalView || 0),
							0
						) ?? 0}
					</Badge> */}
				</div>
			</div>
			<DecorationTab tabs={tabs} />
			<style>
				{`
					@keyframes skeleton-loading {
						0% {
							background-position: -200px 0;
						}
						100% {
							background-position: calc(200px + 100%) 0;
						}
					}
				`}
			</style>
		</Card>
	);
};

PartnerDecorationBox.displayName = 'PartnerDecorationBox';
